import joi from 'joi';
import { Button, Skeleton, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { QlfTitle, colorPalette, useAlerts } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPeople } from '@fortawesome/pro-solid-svg-icons/faPeople';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { isAxiosError } from 'axios';
import { makeStyles } from '@mui/styles';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../../libs/shared/layout/DetailForm';
import { Loader } from '../../libs/shared/layout/Loader';
import { LoginPageForm } from '../../libs/access/components/detail/LoginPageForm';
import {
  ProgramLoginPageProvider,
  useProgramLoginPage,
} from '../../libs/access/ProgramLoginPageProvider';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { useApi } from '../../libs/shared/api-client/ApiClientProvider';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useJoiResolver } from '../../libs/shared/forms/use-joi-resolver';
import { useProgram } from '../../libs/loyalty/ProgramContext';

export enum LoginMethod {
  SSO = 'SSO',
  PAGE = 'PAGE',
  INTEGRATION = 'INTEGRATION',
}

const useStyles = makeStyles(() => ({
  title: {
    textAlign: 'center',
    justifyItems: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  hrSeparation: {
    border: `1px solid ${colorPalette.grey.main}`,
  },
  enable: {
    textAlign: 'center',
    marginTop: '5%',
  },
}));

const loginFormSchema = joi.object({
  loginMethod: joi.string(),
  integrationId: joi.number().optional(),
  pageId: joi.number().optional(),
});

export interface LoginFormValue {
  loginMethod: LoginMethod;
  integrationId?: number;
  pageId?: number;
}

export function ProgramLoginIndexPage() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { features } = useFeatures();
  const { write: programLoginWrite } = features[Feature.PROGRAM_LOGIN];
  const { program, setProgram } = useProgram();
  const { gamificationApi, managerApi } = useApi();
  const { client, setClient, updateSocialLoginClient } = useProgramLoginPage();
  const { notify } = useAlerts();

  const form = useForm<LoginFormValue>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      loginMethod: LoginMethod.SSO,
    },
    resolver: useJoiResolver(loginFormSchema),
  });

  const onError = (error: Error) => {
    if (isAxiosError(error) && error.response?.status === 400) {
      notify(t('access.notify.enable.error'), 'error');
    } else {
      notify(t('access.notify.save.error'), 'error');
    }
  };

  const {
    formState: { isDirty, isSubmitting },
    setValue,
  } = form;
  const onSubmit = async (loginFormInput: LoginFormValue) => {
    const formLoginMethod = loginFormInput.loginMethod;
    const formLoginPageId =
      formLoginMethod === LoginMethod.PAGE ? loginFormInput.pageId ?? null : null;
    const formLoginIntegrationId =
      formLoginMethod === LoginMethod.INTEGRATION ? loginFormInput.integrationId ?? null : null;

    if (isDirty) {
      try {
        if (formLoginMethod === LoginMethod.SSO) {
          const updatedProgram = await managerApi.disableSocialLogin(program.id);
          setProgram(updatedProgram);
        } else {
          const updatedProgram = await updateSocialLoginClient.mutateAsync({
            program,
            body: { pageId: formLoginPageId, integrationId: formLoginIntegrationId },
          });

          if (client?.data) {
            setClient({
              ...client.data,
              pageId: formLoginPageId,
              integrationId: formLoginIntegrationId,
            });
          }
          setProgram(updatedProgram);
        }

        notify(t('shared.actions.saved'), 'success');
        return gamificationApi.markProgramForRefresh(program.id);
      } catch (e: any) {
        onError(e);
      }
    }
  };

  /**
   * @deprecated
   */
  const enableSocialLogin = () => {
    updateSocialLoginClient.mutate(
      {
        program,
        body: {},
      },
      {
        onSuccess: () => {
          setProgram({
            ...program,
            ssoProxyClientId: program.ssoProxyClientId,
          });
          notify(t('access.notify.enable.success'), 'success');
        },
        onError,
      },
    );
  };

  useEffect(() => {
    if (!client.data) {
      return;
    }

    let loginMethod: LoginMethod = LoginMethod.SSO;
    if (typeof client.data.pageId === 'number') {
      setValue('pageId', client.data.pageId);
      loginMethod = LoginMethod.PAGE;
    } else if (typeof client.data.integrationId === 'number') {
      setValue('integrationId', client.data.integrationId);
      loginMethod = LoginMethod.INTEGRATION;
    }
    setValue('loginMethod', loginMethod);
  }, [setValue, client.data]);

  return (
    <QlfMasterDetailContainer>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, console.error)} style={{ height: '100%' }}>
          <DetailForm>
            <DetailForm.Form>
              <div className={classes.title}>
                <QlfTitle variant="h2" separator={{ state: 'center' }}>
                  {t('topbar.tabs.settings.login')}
                </QlfTitle>
              </div>
              <div className={classes.description}>
                {t('program.settings.program-login.description')}
              </div>
              {client.data ? (
                <LoginPageForm />
              ) : (
                <div className={classes.enable}>
                  <Button
                    color="primary"
                    startIcon={
                      updateSocialLoginClient.isPending ? (
                        <Loader />
                      ) : (
                        <FontAwesomeIcon type="submit" fontSize="inherit" icon={faPeople} />
                      )
                    }
                    onClick={enableSocialLogin}
                  >
                    {t('access-rule.login.enable')}
                  </Button>
                </div>
              )}
            </DetailForm.Form>

            <DetailForm.Footer>
              <Tooltip
                title={programLoginWrite ? '' : t('shared.action.permission.required')}
                placement="left"
              >
                <div>
                  <Button
                    type="submit"
                    startIcon={
                      <FontAwesomeIcon
                        type="submit"
                        fontSize="inherit"
                        icon={isSubmitting ? faSpinner : faCheck}
                        spin={isSubmitting}
                      />
                    }
                    color="success"
                    disabled={!isDirty || isSubmitting || !programLoginWrite}
                  >
                    {t('currencies.save.button')}
                  </Button>
                </div>
              </Tooltip>
            </DetailForm.Footer>
          </DetailForm>
        </form>
      </FormProvider>
    </QlfMasterDetailContainer>
  );
}

export function Wrapper() {
  const { loginPages, client, integrations } = useProgramLoginPage();

  useEffect(() => {
    Promise.all([loginPages.refetch(), integrations.refetch(), client.refetch()]).catch(
      console.error,
    );
  }, []);

  const isLoading = client.isPending || loginPages.isPending || integrations.isPending;

  return isLoading ? <Skeleton /> : <ProgramLoginIndexPage />;
}

export function LoginPage() {
  return (
    <ProgramLoginPageProvider>
      <Wrapper />
    </ProgramLoginPageProvider>
  );
}
